// ============================================================================
// JARVISTRAVEL - INVITATION LINKS
// The links core puts in a trip invitation. The person it was sent to answers
// it here, so it has to survive sign-in, sign-up and the onboarding gate.
// ============================================================================

import { generatePath, matchPath } from 'react-router-dom';

export const INVITATION_PATHS = {
  accept: '/trips/:tripId/collaborators/:collaboratorId/accept',
  decline: '/trips/:tripId/collaborators/:collaboratorId/decline',
} as const;

/** The link that answers an invitation: the router's own pattern, filled in. */
export function invitationPath(answer: keyof typeof INVITATION_PATHS, tripId: string, collaboratorId: string): string {
  return generatePath(INVITATION_PATHS[answer], { tripId, collaboratorId });
}

/** Whether a path is one of those links, read by the router's own matcher. */
export function isInvitationPath(pathname: string): boolean {
  return Object.values(INVITATION_PATHS).some((pattern) => matchPath(pattern, pathname) !== null);
}

/**
 * Whether core marked this link for an address with no JarvisTravel account.
 *
 * Core appends `new=1` to the accept link when the address it mailed the
 * invitation to has no account (JAR-2705, `newAddressMark` in
 * `collaboration/service.go`). It is a flag and never the address itself: the
 * link gets forwarded, pasted and written to referrer logs, and none of those
 * should learn who was invited. There is deliberately no endpoint that answers
 * "is this address registered", so this mark is the only way the web knows.
 *
 * Parsed, not substring-matched. `?newsletter=1`, `?renew=1` and `?new=10` all
 * contain "new=1", and reading any of them as marked would send someone who
 * does have an account to sign-up.
 *
 * An unmarked link, which includes every link sent before this shipped, reads
 * false and keeps today's path.
 */
export function isForNewAddress(search: string): boolean {
  return new URLSearchParams(search).get('new') === '1';
}
